'use client';
import React, { useState, useEffect } from 'react';
import { getHutangListDB, getPiutangListDB } from '@/lib/supabaseData';
import CurrencyDisplay from '@/components/ui/CurrencyDisplay';

interface AgingItem {
  id: string;
  label: string;
  value: number;
  barColor: string;
  textColor: string;
  total: number;
}

function AgingCard({ title, items, accentColor }: { title: string; items: AgingItem[]; accentColor: string }) {
  return (
    <div className="bg-white rounded-2xl p-5 h-full" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-5 rounded-full" style={{ backgroundColor: accentColor }} />
        <h3 className="text-base font-700 text-gray-800">{title}</h3>
      </div>
      <div className="space-y-4">
        {items.map((item) => {
          const pct = item.total > 0 ? (item.value / item.total) * 100 : 0;
          return (
            <div key={item.id}>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs font-500 text-gray-500">{item.label}</span>
                <span className={`text-xs font-700 tabular-nums ${item.textColor}`}>
                  <CurrencyDisplay value={item.value} className={`text-xs font-700 ${item.textColor}`} />
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${item.barColor}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-0.5 text-right">{pct.toFixed(1)}%</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AgingSection() {
  const [agingHutang, setAgingHutang] = useState<AgingItem[]>([]);
  const [agingPiutang, setAgingPiutang] = useState<AgingItem[]>([]);

  useEffect(() => {
    Promise.all([getHutangListDB(), getPiutangListDB()]).then(([hutangList, piutangList]) => {
      const hBelumJatuhTempo = hutangList.filter((h) => h.status === 'Belum Jatuh Tempo').reduce((s, h) => s + h.sisaHutang, 0);
      const h1to7 = hutangList.filter((h) => h.status === '1-7 Hari').reduce((s, h) => s + h.sisaHutang, 0);
      const hOverdue1to30 = hutangList.filter((h) => h.status === 'Overdue' && h.umurHari <= 30).reduce((s, h) => s + h.sisaHutang, 0);
      const hOverdueGt30 = hutangList.filter((h) => h.status === 'Overdue' && h.umurHari > 30).reduce((s, h) => s + h.sisaHutang, 0);
      const totalHutang = hBelumJatuhTempo + h1to7 + hOverdue1to30 + hOverdueGt30;

      setAgingHutang([
        { id: 'ah-1', label: 'Belum Jatuh Tempo', value: hBelumJatuhTempo, barColor: 'bg-emerald-500', textColor: 'text-emerald-600', total: totalHutang },
        { id: 'ah-2', label: '1–7 Hari', value: h1to7, barColor: 'bg-amber-400', textColor: 'text-amber-600', total: totalHutang },
        { id: 'ah-3', label: 'Overdue 1–30 Hari', value: hOverdue1to30, barColor: 'bg-red-400', textColor: 'text-red-500', total: totalHutang },
        { id: 'ah-4', label: 'Overdue >30 Hari', value: hOverdueGt30, barColor: 'bg-red-700', textColor: 'text-red-700', total: totalHutang },
      ]);

      const pBelumJatuhTempo = piutangList.filter((p) => p.status === 'Belum Jatuh Tempo').reduce((s, p) => s + p.sisaPiutang, 0);
      const p1to7 = piutangList.filter((p) => p.status === '1-7 Hari').reduce((s, p) => s + p.sisaPiutang, 0);
      const pOverdue1to30 = piutangList.filter((p) => p.status === 'Overdue' && p.umurHari <= 30).reduce((s, p) => s + p.sisaPiutang, 0);
      const pOverdueGt30 = piutangList.filter((p) => p.status === 'Overdue' && p.umurHari > 30).reduce((s, p) => s + p.sisaPiutang, 0);
      const totalPiutang = pBelumJatuhTempo + p1to7 + pOverdue1to30 + pOverdueGt30;

      setAgingPiutang([
        { id: 'ap-1', label: 'Belum Jatuh Tempo', value: pBelumJatuhTempo, barColor: 'bg-blue-500', textColor: 'text-blue-600', total: totalPiutang },
        { id: 'ap-2', label: '1–7 Hari', value: p1to7, barColor: 'bg-amber-400', textColor: 'text-amber-600', total: totalPiutang },
        { id: 'ap-3', label: 'Overdue 1–30 Hari', value: pOverdue1to30, barColor: 'bg-red-400', textColor: 'text-red-500', total: totalPiutang },
        { id: 'ap-4', label: 'Overdue >30 Hari', value: pOverdueGt30, barColor: 'bg-red-700', textColor: 'text-red-700', total: totalPiutang },
      ]);
    });
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      <AgingCard title="Aging Hutang" items={agingHutang} accentColor="#ef4444" />
      <AgingCard title="Aging Piutang" items={agingPiutang} accentColor="#1a3a6b" />
    </div>
  );
}